import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Upload, Download, Trash2, FileText, Paperclip, Home
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const AttachmentList = () => {
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newAttachmentName, setNewAttachmentName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/attachments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !newAttachmentName) {
      toast.error('Please provide a name and select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', newAttachmentName);
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/attachments`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const newAttachment = await response.json();
        setAttachments(prev => [...prev, newAttachment]);
        setNewAttachmentName('');
        setSelectedFile(null);
        toast.success('Attachment uploaded');
      } else {
        toast.error('Failed to upload attachment');
      }
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await fetch(`${API_URL}/api/attachments/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setAttachments(prev => prev.filter(a => a.attachment_id !== deleteId));
        toast.success('Attachment deleted');
      } else {
        toast.error('Failed to delete attachment');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await fetch(
        `${API_URL}/api/attachments/${attachment.attachment_id}/download`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="attachment-list-page">
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
            className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
            title="Home"
          >
            <Home className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#f5f3ed]">Email Attachments</h1>
            <p className="text-[#f5f3ed]/70 text-sm">Manage documents that can be attached to emails</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Upload Form */}
        <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-8">
          <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Upload New Attachment</h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">Attachment Name</Label>
                <Input
                  value={newAttachmentName}
                  onChange={(e) => setNewAttachmentName(e.target.value)}
                  placeholder="e.g., Terms and Conditions"
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="attachment-name-input"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">File</Label>
                <div className="flex items-center gap-2">
                  <label className="flex-1">
                    <input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    <div className="cursor-pointer bg-[#1a1f1a]/50 border border-[#2d5f3f] rounded-md px-3 py-2 text-[#f5f3ed]/70 text-sm truncate">
                      {selectedFile ? selectedFile.name : 'Choose file...'}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <Button
              type="submit"
              disabled={uploading || !selectedFile || !newAttachmentName}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
              data-testid="upload-attachment-btn"
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a1f1a]" />
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Attachment
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Attachments List */}
        <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
          <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Saved Attachments</h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c9a55a]"></div>
            </div>
          ) : attachments.length === 0 ? (
            <div className="text-center py-8">
              <Paperclip className="w-12 h-12 text-[#f5f3ed]/30 mx-auto mb-3" />
              <p className="text-[#f5f3ed]/50">No attachments uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {attachments.map(attachment => (
                <div
                  key={attachment.attachment_id}
                  className="flex items-center justify-between bg-[#1a1f1a]/50 rounded-lg p-4"
                  data-testid={`attachment-${attachment.attachment_id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-[#c9a55a]" />
                    <div>
                      <p className="text-[#f5f3ed] font-medium">{attachment.name}</p>
                      <p className="text-[#f5f3ed]/50 text-sm">
                        {attachment.original_name} • {formatFileSize(attachment.file_size)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDownload(attachment)}
                      className="text-[#c9a55a] hover:bg-[#c9a55a]/10"
                    >
                      <Download className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(attachment.attachment_id)}
                      className="text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Attachment?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will permanently delete this attachment and remove it from any email templates that use it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AttachmentList;
